import type { DataTableColumn } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import {
  listDiagnosticJobWindowEpisodes,
  type AdminDiagnosticJobWindow,
  type AdminDiagnosticWindowEpisode,
  type DiagnosticWindowEpisodeState,
} from "@/api/adminDiagnosticJob";
import { PaginationFooter } from "@/components/browse/PaginationFooter";
import { HatDataTable } from "@/components/table/HatDataTable";
import { ChevronRightIcon } from "@/components/icons";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatReportedAtAbsolute, workerIdTail } from "./workerStatusFormatters";
import { workerJobStatusLabel } from "./workerJobStatusLabel";
import {
  DiagnosticWindowEpisodesRow,
  DiagnosticWindowTableSource,
  type DiagnosticWindowRow,
} from "./DiagnosticWindowTableSource";
import { usePolledRequest } from "./usePolledRequest";
import detailStyles from "./WorkerDetailContent.module.css";
import styles from "./DiagnosticWindowTable.module.css";
import { tableStripError } from "@/components/table/tableStateMessages";

const POLL_MS = 5_000;
const time = (value: string | null) => (value === null ? "—" : formatReportedAtAbsolute(value));

const COLUMNS: readonly DataTableColumn<DiagnosticWindowRow>[] = [
  {
    key: "ordinal",
    header: "구간",
    width: "88px",
    render: (row) => (
      <span className={styles.ordinal}>
        <span
          className={row.isRowChildrenExpanded ? styles.caretOpen : styles.caret}
          aria-hidden="true"
        >
          <ChevronRightIcon />
        </span>
        #{row.ordinal}
      </span>
    ),
  },
  {
    key: "status",
    header: "상태",
    width: "96px",
    render: (row) => workerJobStatusLabel(row.status),
  },
  {
    key: "processed",
    header: "처리",
    width: "96px",
    render: (row) => `${row.processed} / ${row.window.target_count}`,
  },
  { key: "failed", header: "실패", width: "64px", render: (row) => row.failed },
  {
    key: "worker",
    header: "담당 Worker",
    width: "minmax(180px, 1fr)",
    render: (row) => <WorkerCell window={row.window} />,
  },
  { key: "claims", header: "점유 횟수", width: "88px", render: (row) => row.claims },
  {
    key: "lastReport",
    header: "마지막 보고",
    width: "240px",
    render: (row) => time(row.lastReport),
  },
  {
    key: "reason",
    header: "사유",
    width: "minmax(140px, 1fr)",
    render: (row) => (
      <span className={styles.reason} title={row.reason ?? undefined}>
        {row.reason ?? "—"}
      </span>
    ),
  },
];

/**
 * video 작업의 구간 표다. 구간은 대상 에피소드 묶음이다. Worker 하나가 구간 하나를 점유한다.
 * 행을 누르면 그 구간의 에피소드를 행 바로 아래 펼친다. 이 표는 구간 목록과 펼친 구간의 에피소드를
 * 5초마다 다시 받는다.
 * 모달이 닫히면 c6 Modal 이 이 표를 언마운트한다. 그래서 폴링은 마운트된 동안만 돈다.
 */
export function DiagnosticWindowTable({ jobUuid }: { jobUuid: string }): ReactNode {
  const source = useMemo(() => new DiagnosticWindowTableSource(jobUuid), [jobUuid]);
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  useEffect(() => () => source.dispose(), [source]);
  useEffect(() => {
    void source.initialize();
    const refresh = () => {
      if (document.visibilityState !== "hidden" && source.getSnapshot().loadState !== "loading")
        void source.refresh();
    };
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [source]);
  const loading = snapshot.loadState === "loading";
  const stripError = tableStripError(snapshot);
  return (
    <section className={detailStyles.section} aria-label="구간">
      <h3 className={detailStyles.sectionTitle}>구간</h3>
      <div className={styles.body}>
        {/* 행이 없는 조회 실패는 표 본문이 알린다. 이 줄은 구간이 남아 있는 갱신 실패를 알린다. */}
        {stripError !== null && (
          <p role="alert" className={detailStyles.error}>
            {stripError}
          </p>
        )}
        <div className={detailStyles.table}>
          <HatDataTable
            source={source}
            columns={COLUMNS}
            ariaLabel="구간 테이블"
            emptyMessage="아직 할당한 구간이 없습니다."
            loadingMessage="구간을 불러오고 있습니다."
            minWidth="960px"
            showIndex={false}
            scrollable
            renderRowChild={(child) =>
              child instanceof DiagnosticWindowEpisodesRow ? (
                <WindowEpisodes jobUuid={jobUuid} window={child.window} />
              ) : null
            }
          />
        </div>
        <PaginationFooter
          page={snapshot.page}
          size={snapshot.size}
          total={snapshot.total}
          isLoading={loading}
          ariaLabel="구간 페이지 이동"
          hideWhenSinglePage
          onPrevious={() => void source.goToPage(snapshot.page - 1)}
          onNext={() => void source.goToPage(snapshot.page + 1)}
        />
      </div>
    </section>
  );
}

// 실행 중인 구간의 담당 Worker 가 보고하지 않으면 경고로 알린다. 판정은 서버가 한다.
function WorkerCell({ window }: { window: AdminDiagnosticJobWindow }): ReactNode {
  if (window.worker_id === null) return "—";
  const silent = window.status === "running" && !window.worker_reporting;
  return (
    <span className={styles.worker}>
      <span className={detailStyles.mono} title={window.worker_id}>
        {workerIdTail(window.worker_id)}
      </span>
      {silent && <span className={styles.silent}>보고 없음</span>}
    </span>
  );
}

const EPISODE_STATE_LABELS: Record<DiagnosticWindowEpisodeState, string> = {
  done: "완료",
  failed: "실패",
  pending: "대기",
};
const EPISODE_STATE_GLYPHS: Record<DiagnosticWindowEpisodeState, string> = {
  done: "✓",
  failed: "✗",
  pending: "·",
};

// 펼친 구간의 에피소드다. 이름 번호와 순번이 어긋날 수 있다. 그래서 범위 대신 실제 대상을 모두 보인다.
function WindowEpisodes({
  jobUuid,
  window,
}: {
  jobUuid: string;
  window: AdminDiagnosticJobWindow;
}): ReactNode {
  const request = useCallback(
    () => listDiagnosticJobWindowEpisodes(jobUuid, window.uuid),
    [jobUuid, window.uuid],
  );
  const { data: episodes, error } = usePolledRequest(request);
  const counts = useMemo(() => countEpisodeStates(episodes ?? []), [episodes]);
  return (
    <div className={styles.episodes}>
      <p className={styles.facts}>
        시작 {time(window.started_at)} · 종료 {time(window.finished_at)} · 점유 세대{" "}
        {window.claim_generation}
      </p>
      {error !== null && (
        <EmptyState size="sm" mode="plain" tone="error">
          {error}
        </EmptyState>
      )}
      {episodes === null && error === null && (
        <LoadingState size="sm" mode="plain">
          구간의 에피소드를 불러오고 있습니다.
        </LoadingState>
      )}
      {episodes !== null && (
        <>
          <p className={styles.facts}>
            대상 {episodes.length} · 완료 {counts.done} · 실패 {counts.failed} · 대기{" "}
            {counts.pending}
          </p>
          <ul className={styles.episodeList} aria-label={`구간 ${window.ordinal} 에피소드`}>
            {episodes.map((episode) => (
              <li
                key={episode.episode_uuid}
                className={styles.episode}
                data-state={episode.state}
                title={`${episode.name} · ${EPISODE_STATE_LABELS[episode.state]}`}
              >
                <span role="img" aria-label={EPISODE_STATE_LABELS[episode.state]}>
                  {EPISODE_STATE_GLYPHS[episode.state]}
                </span>
                {episode.name}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function countEpisodeStates(episodes: readonly AdminDiagnosticWindowEpisode[]) {
  const counts: Record<DiagnosticWindowEpisodeState, number> = { done: 0, failed: 0, pending: 0 };
  for (const episode of episodes) counts[episode.state] += 1;
  return counts;
}
